import { CircleInfoIcon } from "@xola/icons";
import { Alert, Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, now, requiredEmail, requiredString, SecondaryButton } from "common";
import dayjs from "dayjs";
import { isEmpty, kebabCase } from "lodash";
import React, { FC, useState } from "react";
import { Except } from "type-fest";
import { Field, FieldType, WaiverParticipant } from "types";
import * as yup from "yup";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { DateOfBirthField, getISOFormattedDateString } from "@/components/Questionnaire/DateOfBirthField";
import { fieldMap } from "@/components/Questionnaire/QuestionnaireModal";
import { SelectField } from "@/components/Questionnaire/SelectField";
import { TextareaField } from "@/components/Questionnaire/TextareaField";
import { FieldProps, TextField } from "@/components/Questionnaire/TextField";
import { defaultField } from "@/models/Form/Field";

interface WaiverCorrectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    participant: WaiverParticipant;
    onSubmit: (participant: WaiverParticipant) => Promise<void> | void;
    isSaving?: boolean;
    isSuccess?: boolean;
}

// Incase the questionnaire have phone / date of birth field, then we need to show it only once.
// While saving we need to update both fields.
const updateParticipantAnswers = (participant: WaiverParticipant): WaiverParticipant => {
    if (!participant.answers) return participant;

    const typeToFieldMap: Record<string, keyof WaiverParticipant> = {
        phone: "phone",
        email: "customerEmail",
        dateOfBirth: "dateOfBirth",
    };

    const updatedAnswers = participant.answers.map((answer) => {
        const fieldKey = typeToFieldMap[answer.type];
        const newValue = fieldKey ? participant[fieldKey] : undefined;

        return {
            ...answer,
            value: typeof newValue === "string" ? newValue : answer.value,
        };
    });

    return {
        ...participant,
        answers: updatedAnswers,
    };
};

export const WaiverCorrectionModal = ({
    isOpen,
    participant,
    isSaving = false,
    isSuccess = false,
    onClose,
    onSubmit,
}: WaiverCorrectionModalProps) => {
    const handleSubmit = async (participant: WaiverParticipant) => {
        await onSubmit(participant);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent
                participant={participant}
                isSaving={isSaving}
                isSuccess={isSuccess}
                onClose={onClose}
                onSubmit={handleSubmit}
            />
        </Modal>
    );
};

const ModalContent = ({
    participant: initialValues,
    isSaving,
    isSuccess,
    onClose,
    onSubmit,
}: Except<WaiverCorrectionModalProps, "isOpen">) => {
    const [participantFields] = useState(() => getParticipantFields(initialValues));

    const handleSubmit = (values: WaiverParticipant) => {
        const formattedValues = updateParticipantAnswers(values);

        // Handle empty name
        if (isEmpty(formattedValues.customerName?.trim())) {
            formattedValues.customerName = initialValues.customerName;
        }

        // Format the date of birth to YYYY-MM-DD (ISO format) if it exists
        // DateOfBirthField returns MM/DD/YYYY format, so we convert to ISO before sending
        if (formattedValues.dateOfBirth) {
            formattedValues.dateOfBirth = getISOFormattedDateString(formattedValues.dateOfBirth);
        }

        onSubmit(formattedValues);
    };

    return (
        <XolaFormik initialValues={initialValues} validationSchema={validateSchema} onSubmit={handleSubmit}>
            {({ handleSubmit, dirty }) => (
                <>
                    <Modal.Header>Waiver Corrections</Modal.Header>
                    <Modal.Body>
                        <Alert className="mb-8" color="warning">
                            <div className="ml-6 flex items-center gap-4">
                                <span>
                                    <CircleInfoIcon size="large" />
                                </span>
                                <span>
                                    Please limit your edits to corrections only since a new signature will <b>not</b> be
                                    collected from the customer.
                                </span>
                            </div>
                        </Alert>
                        <div className="max-h-[70vh] overflow-y-auto">
                            <FormField label="Name" name="customerName">
                                <InputField isRequired />
                            </FormField>

                            <FormField label="Email" name="customerEmail">
                                <InputField isRequired type="email" />
                            </FormField>

                            {initialValues.phone !== undefined && (
                                <FormField label="Phone" name="phone">
                                    <InputField type="tel" />
                                </FormField>
                            )}

                            <FormField name="dateOfBirth">
                                <DateOfBirthField
                                    name="dateOfBirth"
                                    field={
                                        {
                                            ...defaultField,
                                            id: "dateOfBirth",
                                            label: "Date of Birth",
                                        } as Field
                                    }
                                />
                            </FormField>

                            {participantFields.map((answer) => {
                                const originalAnswer =
                                    initialValues.answers?.find(
                                        (a) => a.type === answer.type && a.label === answer.label,
                                    ) ?? answer;

                                const originalIndex =
                                    initialValues.answers?.findIndex(
                                        (a) => a.type === answer.type && a.label === answer.label,
                                    ) ?? -1;

                                const fieldType = answerToFieldTypeMap[answer.type] || FieldType.TEXT;
                                const fieldKey = kebabCase(`${answer.type}-${answer.label}`);

                                let InputComponent: FC<FieldProps>;
                                if (fieldType === FieldType.TEXT && answer.value?.length > 60) {
                                    InputComponent = TextareaField;
                                } else if (fieldType === FieldType.SELECT && answer.options?.length) {
                                    InputComponent = SelectField;
                                } else {
                                    InputComponent = fieldMap[fieldType] ?? TextField;
                                }

                                const isRequired = !!answer.required;

                                return (
                                    <div key={fieldKey} data-testid="waiver-correction-modal-form-field">
                                        <FormField name={`answers.${originalIndex}.value`}>
                                            <InputComponent
                                                name={`answers.${originalIndex}.value`}
                                                field={{
                                                    ...defaultField,
                                                    id: `answer-${originalIndex}`,
                                                    type: fieldType,
                                                    value: originalAnswer.value,
                                                    label: answer.label,
                                                    ...(answer.unit ? { unit: answer.unit } : {}),
                                                }}
                                                isRequired={isRequired}
                                                classNames={{ label: "overflow-hidden text-ellipsis no-translate" }}
                                            />
                                        </FormField>
                                    </div>
                                );
                            })}
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton disabled={isSaving} onClick={onClose}>
                            Cancel
                        </SecondaryButton>

                        <SubmitButton
                            isLoading={isSaving}
                            isSuccess={isSuccess}
                            disabled={!dirty}
                            onClick={handleSubmit}
                        >
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const defaultFieldTypes = new Set(["phone", "email", "dateOfBirth"]);

const getParticipantFields = (
    participant: WaiverParticipant,
): Array<NonNullable<WaiverParticipant["answers"]>[number]> => {
    if (!participant.answers || participant.answers.length === 0) {
        return [];
    }

    // Incase the questionnaire has phone / date of birth / email field, we show it only once.
    return participant.answers.filter((answer) => !defaultFieldTypes.has(answer.type));
};

const ANSWER_TYPE = {
    TEXT: "text",
    EMAIL: "email",
    TEXTAREA: "textarea",
    SELECT: "select",
    CHECKBOX: "checkbox",
    MARKETING_CONSENT: "marketingConsent",
    DATE_OF_BIRTH: "dateOfBirth",
    HEIGHT: "height",
    WEIGHT: "weight",
    PHONE: "phone",
} as const;

const answerToFieldTypeMap: Record<string, FieldType> = {
    [ANSWER_TYPE.TEXT]: FieldType.TEXT,
    [ANSWER_TYPE.EMAIL]: FieldType.EMAIL,
    [ANSWER_TYPE.TEXTAREA]: FieldType.TEXTAREA,
    [ANSWER_TYPE.SELECT]: FieldType.SELECT,
    [ANSWER_TYPE.CHECKBOX]: FieldType.CHECKBOX,
    [ANSWER_TYPE.MARKETING_CONSENT]: FieldType.MARKETING_CONSENT,
    [ANSWER_TYPE.DATE_OF_BIRTH]: FieldType.DATE_OF_BIRTH,
    [ANSWER_TYPE.HEIGHT]: FieldType.HEIGHT,
    [ANSWER_TYPE.WEIGHT]: FieldType.WEIGHT,
    [ANSWER_TYPE.PHONE]: FieldType.PHONE,
};

const validateSchema = yup.object().shape({
    customerName: requiredString(),
    customerEmail: requiredEmail(),
    dateOfBirth: yup.string().test("is-valid-date", "is invalid", (value) => {
        if (!value) return true;
        // Convert to ISO format first, then validate
        const isoDate = getISOFormattedDateString(value);
        const date = dayjs(isoDate, "YYYY-MM-DD", true);
        return date.isValid() && date.isBefore(now());
    }),
    answers: yup.array().of(
        yup.object().shape({
            value: yup
                .string()
                .nullable()
                .when(["required"], {
                    is: (required: boolean) => required,
                    then: yup.string().trim().required("is required"),
                })
                .when("validation.minLength", (minLength, schema) =>
                    minLength ? schema.min(minLength, `must be at least ${minLength} characters`) : schema,
                )
                .when("validation.maxLength", (maxLength, schema) =>
                    maxLength ? schema.max(maxLength, `must be no more than ${maxLength} characters`) : schema,
                )
                .when("validation.pattern", (pattern, schema) =>
                    pattern ? schema.matches(new RegExp(pattern), "is in invalid format") : schema,
                ),
        }),
    ),
});
